import React from 'react';
import * as Diff from 'diff';

const AllVersionsCard = ({ synopsisVersions, onVersionSelect }) => {
  // Format date helper
  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch {
      return dateStr;
    }
  };
  
  // Generate diff preview comparing current version with previous version
  const generateDiffPreview = (currentText, previousText) => {
    if (!previousText) {
      // First version - no comparison, show full text
      return <span className="text-gray-700 dark:text-neutral-300">{currentText}</span>;
    }
    
    // Calculate word-level diff
    const diff = Diff.diffWords(previousText, currentText);
    
    const elements = [];
    
    for (let i = 0; i < diff.length; i++) {
      const part = diff[i];
      
      if (part.added) {
        // Added text - no special styling in preview (just show it)
        elements.push(
          <span key={i} className="text-gray-700 dark:text-neutral-300">
            {part.value}
          </span>
        );
      } else if (part.removed) {
        // Removed text - show with strikethrough and red color
        elements.push(
          <span key={i} className="line-through text-red-600 dark:text-red-400">
            {part.value}
          </span>
        );
      } else {
        // Unchanged text
        elements.push(
          <span key={i} className="text-gray-700 dark:text-neutral-300">
            {part.value}
          </span>
        );
      }
    }
    
    return <>{elements}</>;
  };
  
  if (!synopsisVersions || synopsisVersions.length === 0) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-sm font-semibold text-gray-900 mb-2">Editing History</h3>
        <div className="text-center py-8">
          <p className="text-gray-600">No versions available yet</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="bg-white dark:bg-neutral-950 rounded-lg border border-gray-200 dark:border-neutral-800 p-2">
      <h3 className="text-sm font-semibold text-gray-900 dark:text-neutral-100 mb-2">Editing History</h3>
      
      <div className="border border-gray-200 dark:border-neutral-800 rounded-lg overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-800">
          <thead className="bg-gray-50 dark:bg-neutral-900 text-xs text-gray-500 dark:text-neutral-400">
            <tr>
              <th className="px-4 py-2 text-left tracking-wider">
                Version
              </th>
              <th className="px-4 py-2 text-left tracking-wider">
                Synopsis Preview
              </th>
              <th className="px-4 py-2 text-left tracking-wider">
                Date
              </th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-neutral-950 divide-y divide-gray-200 dark:divide-gray-800">
            {[...synopsisVersions].reverse().map((version, reversedIndex) => {
              const originalIndex = synopsisVersions.length - 1 - reversedIndex;
              const versionNum = synopsisVersions.length - reversedIndex;
              const synopsisText = version.synopsis_text || 'No synopsis available';
              
              // Get previous version for comparison (if exists)
              const previousVersion = originalIndex > 0 ? synopsisVersions[originalIndex - 1] : null;
              const previousText = previousVersion?.synopsis_text || '';
              
              // Generate diff preview
              const diffPreview = generateDiffPreview(synopsisText, previousText);
              
              return (
                <tr
                  key={version.id || originalIndex}
                  onClick={() => onVersionSelect(originalIndex)}
                  className="cursor-pointer hover:bg-sky-50 dark:hover:bg-neutral-900 transition-colors"
                >
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200">
                        v{versionNum}
                      </span>
                      {previousVersion && (
                        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200">
                          vs {versionNum - 1}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <div className="whitespace-pre-wrap">{diffPreview}</div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-500 dark:text-neutral-400">
                    {formatDate(version.created_at)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AllVersionsCard;

